import React from 'react';
import Script from 'next/script';
import { AuthCard } from '@/components/auth/AuthCard';
import { AuthLayout } from '@/components/layout/layout/AuthLayout';
import { useAuth } from '@/hooks/useAuth';
import NextAuthProvidersEnum from '@/const/auth';
import InputPassword from 'components/form/input-password/input-password';
import { InputEmail } from 'components/form';
import { Typography } from '@/components/common';
import { FieldValues } from 'react-hook-form';

const rules = {
	email: {
		required: { value: true, message: 'Please enter a valid email address.' },
	},
	password: {
		required: {
			value: true,
			message:
				'Your password does not meet the minimum requirements. It must be at least 8 characters long and include one number, one uppercase letter, and one special character (_-:*).',
		},
		maxLength: {
			value: 30,
			message: 'Your password must be 30 characters or less',
		},
		minLength: {
			value: 8,
			message:
				'Your password does not meet the minimum requirements. It must be at least 8 characters long and include one number, one uppercase letter, and one special character (_-:*).',
		},
		validate: (value: string) => {
			const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
			if (!passwordRegex.test(value)) {
				return 'Your password does not meet the minimum requirements. It must be at least 8 characters long and include one number, one uppercase letter, and one special character (_-:*).';
			}

			return undefined;
		},
	},
};

export const SecondSignUpScreen = () => {
	const {
		form,
		isLoading,
		recaptchaValid,
		handleSubmitData,
		onChangeRecaptcha,
	} = useAuth();

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
	} = form;

	const onSubmit = (
		provider: NextAuthProvidersEnum,
		data?: FieldValues
	): void => {
		const payload = {
			email: data?.email ?? '',
			password: data?.password ?? '',
			confirmPassword: data?.password ?? '',
		};
		handleSubmitData(provider, payload);
	};

	return (
		<AuthLayout>
			<AuthCard
				disabled={isLoading || !recaptchaValid || !isDirty}
				title="Get Started"
				isLoading={isLoading}
				handleSubmit={handleSubmit}
				onChangeRecaptcha={onChangeRecaptcha}
				handleSubmitData={onSubmit}
				buttonLabel="Create an Account"
				formId="sign-up-form"
			>
				{/* AdQuire TruFilter FormCheck: START - Place this code inside the form to be checked */}
				{process.env.NEXT_PUBLIC_SERVER === 'production' && (
					<Script
						id="PD_Script_Configs"
						defer
						data-cfasync="false"
						src="https://forms.adq.us.cdn-world.net/CLA/configs.js"
						strategy="afterInteractive"
					/>
				)}
				{/* AdQuire TruFilter FormCheck: END */}
				<div className="space-y-6">
					<InputEmail
						name="email"
						title="Email"
						register={register}
						rules={rules.email}
						error={errors.email}
						setValueCustom={setValue}
						classNameInput="shadow-shadow2 lg:shadow-none"
					/>
					<div>
						<InputPassword
							name="password"
							title="Create a Password"
							register={register}
							validate={false}
							rules={rules.password}
							error={errors.password}
							classNameInput="shadow-shadow2 lg:shadow-none"
						/>
						{!errors?.password && (
							<Typography type="regular-10" className="text-gray-35 mt-2">
								8 characters minimum, One number, One uppercase letter, One
								special character (_-:*).
							</Typography>
						)}
					</div>
				</div>
			</AuthCard>
		</AuthLayout>
	);
};
